import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @param {number} cents
 * @returns {string}
 */
function formatMoney(cents) {
  if (typeof cents !== 'number') cents = parseInt(String(cents), 10);
  if (isNaN(cents)) return '';
  const val = (cents / 100).toFixed(2);
  const [i, d] = val.split('.');
  return '₺' + (i || '0').replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + (d || '00');
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors
 * @property {HTMLTableRowElement[]} cartItemRows
 * @property {TextComponent} cartTotal
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({ line, quantity, action: 'change' });

    const lineItemRow = this.refs.cartItemRows?.[line - 1];
    if (!lineItemRow) return;
    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /** @param {number} line */
  onLineItemRemove(line) {
    this.updateQuantity({ line, quantity: 0, action: 'clear' });

    const cartItemRowToRemove = this.refs.cartItemRows?.[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * @param {{line: number, quantity: number, action: string}} config
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const cart = JSON.parse(responseText);
        resetShimmer(this);

        if (cart.errors) {
          this.#handleCartError(line, cart);
          return;
        }

        // Step 1: Instant visual update from JSON — works immediately
        this.#updatePricesFromCartJSON(cart);

        // Step 2: Full section DOM update from bundled HTML
        const sectionHtml = cart.sections?.[this.sectionId];
        if (sectionHtml) {
          try {
            morphSection(this.sectionId, sectionHtml);
          } catch (_e) {
            // morphSection can fail if section wrapper ID doesn't match; fall back
            sectionRenderer.renderSection(this.sectionId, { cache: false });
          }
        } else {
          // No bundled HTML in response; fetch from server
          sectionRenderer.renderSection(this.sectionId, { cache: false });
        }

        // Step 3: Notify other components (cart icon bubble, cart drawer, etc.)
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: cart.item_count ?? 0,
            source: 'cart-items-component',
            sections: cart.sections ?? {},
          })
        );
      })
      .catch((error) => {
        console.error('Cart update error:', error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  handleDiscountUpdate = (/** @type {DiscountUpdateEvent} */ event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * @param {number} line
   * @param {{errors: string}} cart
   */
  #handleCartError = (line, cart) => {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (cartItemError instanceof HTMLElement) cartItemError.textContent = cart.errors;
    if (cartItemErrorContainer instanceof HTMLElement) cartItemErrorContainer.classList.remove('hidden');
  };

  /** @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Immediately updates all visible prices from the cart JSON response.
   * This gives instant feedback BEFORE morphSection replaces the DOM.
   * @param {{items: Array<any>, items_subtotal_price?: number, total_price?: number, total_tax?: number, item_count?: number}} cart
   */
  #updatePricesFromCartJSON(cart) {
    if (!cart.items) return;

    // --- CART ITEMS (left column) ---
    for (const item of cart.items) {
      const key = String(item.key);
      const row = this.querySelector('.cart-item-card[data-key="' + key + '"]');
      if (!row) continue;

      const priceEl = row.querySelector('.card__price .current-price');
      if (priceEl) priceEl.textContent = formatMoney(item.final_line_price);

      const compareEl = row.querySelector('.card__price .compare-price');
      if (compareEl instanceof HTMLElement) {
        const orig = item.original_line_price ?? 0;
        compareEl.textContent = orig > item.final_line_price ? formatMoney(orig) : '';
        compareEl.style.display = orig > item.final_line_price ? '' : 'none';
      }
    }

    // --- SUMMARY ITEMS (right column) ---
    for (const item of cart.items) {
      const key = String(item.key);
      const el = this.querySelector('.summary-item[data-key="' + key + '"]');
      if (!el) continue;

      const priceEl = el.querySelector('.summary-item-price');
      if (priceEl) priceEl.textContent = formatMoney(item.final_line_price);

      const qtyEl = el.querySelector('.summary-item__quantity');
      if (qtyEl) qtyEl.textContent = item.quantity + ' Adet';
    }

    // --- SUMMARY TOTALS ---
    const subtotal = cart.items_subtotal_price ?? 0;

    this.querySelectorAll('[ref="cartSubtotal"]').forEach((el) => {
      el.textContent = formatMoney(subtotal);
    });

    const taxEl = this.querySelector('[ref="cartTax"]');
    if (taxEl) {
      taxEl.textContent = formatMoney(cart.total_tax ?? Math.round(subtotal * 0.1));
    }

    const total = cart.total_price ?? subtotal;
    this.querySelectorAll('[ref="cartTotal"]').forEach((el) => {
      if (el instanceof HTMLElement) el.textContent = formatMoney(total);
    });
    this.querySelectorAll('.summary-grand-total .total-price').forEach((el) => {
      el.textContent = formatMoney(total);
    });

    // Compare price total
    let compareTotal = 0;
    for (const item of cart.items) {
      const orig = item.original_line_price ?? 0;
      if (orig > item.final_line_price) compareTotal += orig;
    }
    const compareTotalEl = this.querySelector('.compare-price-total');
    if (compareTotalEl) {
      compareTotalEl.textContent = compareTotal > 0 ? formatMoney(compareTotal) : '';
    }

    // Cart item count
    const countEl = this.querySelector('[ref="cartItemCount"]');
    if (countEl) countEl.textContent = String(cart.item_count ?? 0);
  }

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /** @returns {string} */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
